import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarHeart, ChefHat, Cake, Star } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useCalAuth } from "@/lib/calAuth";
import { calFetch, localYmd } from "@/lib/calApi";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 20) return "Buenas tardes";
  return "Buenas noches";
}

function TodayTeaser() {
  const [today, setToday] = useState(null);
  useEffect(() => {
    calFetch(`/cal/today?date=${localYmd()}`).then((r) => setToday(r.today)).catch(() => setToday(false));
  }, []);
  if (today === null) return <div className="h-16 rounded-xl bg-white/5 animate-pulse" />;
  if (!today) return null;
  const fam = today.events.filter((e) => ["cumple", "aniversario", "especial", "yahrzeit", "fiesta"].includes(e.kind)).slice(0, 3);
  return (
    <div data-testid="hub-today-teaser" className="rounded-xl bg-white/[0.06] border border-white/10 p-4 space-y-2">
      <p className="text-[11px] uppercase tracking-[0.2em] text-candle/80">Hoy · {today.hebrew_label}</p>
      {fam.length ? fam.map((e, i) => (
        <p key={i} className="flex items-start gap-2 text-sm text-white/90">
          {e.kind === "cumple" ? <Cake className="w-4 h-4 mt-0.5 text-candle shrink-0" /> : <Star className="w-4 h-4 mt-0.5 text-candle shrink-0" />}
          <span>{e.kind === "cumple" ? `Hoy es ${e.phrase}` : e.title}</span>
        </p>
      )) : <p className="text-sm text-white/70">Hoy no hay cumpleaños en la familia. Entra a ver qué viene.</p>}
    </div>
  );
}

function CalendarPortal() {
  const { token, person } = useCalAuth();
  return (
    <Link to={token ? "/calendario" : "/calendario/entrar"} data-testid="hub-calendar-card"
      className="cal-rise cal-press group relative overflow-hidden rounded-[28px] bg-night cal-grain p-7 sm:p-9 flex flex-col justify-between min-h-[420px] shadow-xl"
      style={{ animationDelay: "120ms" }}>
      <div className="absolute inset-0 cal-stars pointer-events-none" />
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-candle/20 blur-3xl cal-glow pointer-events-none" />
      <div className="relative">
        <div className="w-12 h-12 rounded-2xl bg-candle/15 border border-candle/30 flex items-center justify-center mb-6">
          <CalendarHeart className="w-6 h-6 text-candle" strokeWidth={1.5} />
        </div>
        <p className="text-[11px] uppercase tracking-[0.25em] text-candle/80 mb-2">Toda la familia · 3 generaciones</p>
        <h2 className="font-display italic text-white text-4xl sm:text-5xl leading-[1.05] mb-3">Calendario Familiar</h2>
        <p className="text-white/70 text-sm sm:text-base max-w-sm leading-relaxed">
          Lo que pasa hoy en la familia: cumpleaños gregorianos y hebreos, aniversarios, fiestas judías y saludos.
        </p>
      </div>
      <div className="relative mt-8 space-y-4">
        {token && <TodayTeaser />}
        <span className="inline-flex items-center gap-2 rounded-full bg-candle text-night px-5 py-3 text-sm font-semibold group-hover:gap-3 transition-[gap]">
          {token ? `Ver el día de hoy${person ? `, ${person.nickname || person.name.split(" ")[0]}` : ""}` : "Entrar o crear mi clave"}
          <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}

function RecetarioPortal() {
  const { token } = useAuth();
  return (
    <Link to={token ? "/cocina" : "/login"} data-testid="hub-recetario-card"
      className="cal-rise cal-press group relative overflow-hidden rounded-[28px] bg-cream border border-line p-7 sm:p-9 flex flex-col justify-between min-h-[420px] shadow-sm"
      style={{ animationDelay: "240ms" }}>
      <img src="/alan_chef.jpg" alt="El Pollo cocinando" className="absolute right-0 bottom-0 w-1/2 h-full object-cover object-top opacity-25 group-hover:opacity-35 transition-opacity [mask-image:linear-gradient(to_left,black,transparent)]" />
      <div className="relative">
        <div className="w-12 h-12 rounded-2xl bg-amber-deep flex items-center justify-center mb-6">
          <ChefHat className="w-6 h-6 text-paper" strokeWidth={1.5} />
        </div>
        <p className="eyebrow text-[11px] mb-2">De papá a hijos</p>
        <h2 className="font-display italic text-cocoa text-4xl sm:text-5xl leading-[1.05] mb-3">El Recetario del Pollo</h2>
        <p className="font-serif text-cocoa-soft text-base sm:text-lg max-w-xs leading-relaxed">
          Las recetas de la casa, contadas por Papi. Con modo cocina, menú semanal y mucho cariño.
        </p>
      </div>
      <span className="relative mt-8 inline-flex items-center gap-2 self-start rounded-full bg-cocoa text-paper px-5 py-3 text-sm font-semibold group-hover:gap-3 transition-[gap]">
        {token ? "Ir a la cocina" : "Entrar al recetario"} <ArrowRight className="w-4 h-4" />
      </span>
    </Link>
  );
}

export default function Hub() {
  return (
    <div data-testid="hub-page" className="min-h-screen paper-texture iphone-safe-top">
      <main className="max-w-6xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-16">
        <header className="cal-rise mb-10 sm:mb-14 max-w-2xl">
          <p className="eyebrow text-[11px] mb-3">Familia Guendelman Portugues</p>
          <h1 className="font-display italic text-cocoa text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
            {greeting()}, familia.
          </h1>
          <p className="font-serif text-cocoa-soft text-base md:text-lg mt-4">¿Pa' dónde vamos hoy?</p>
        </header>
        <div className="grid md:grid-cols-[1.15fr_1fr] gap-5 sm:gap-6">
          <CalendarPortal />
          <RecetarioPortal />
        </div>
      </main>
    </div>
  );
}
